import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
    ArrowLeft, Plus, Loader2, Trash2, Pencil, ShieldCheck, Package, X, Save,
} from "lucide-react";

const ROLE_LABEL = {
    super_admin: { label: "Super Admin", tone: "bg-primary text-primary-foreground", icon: ShieldCheck },
    product_admin: { label: "Admin Produk", tone: "bg-accent/15 text-accent border border-accent/30", icon: Package },
};

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (u) => {
        if (!window.confirm(`Hapus user "${u.email}"?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            setUsers((arr) => arr.filter((x) => x.id !== u.id));
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    if (me?.role !== "super_admin") {
        return (
            <div className="mx-auto max-w-2xl px-5 py-16 text-center">
                <h1 className="font-heading text-2xl font-semibold">Akses Ditolak</h1>
                <p className="mt-2 text-muted-foreground">Hanya Super Admin yang boleh mengelola user.</p>
                <Link to="/admin" className="mt-6 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
                    <ArrowLeft size={14} /> Kembali ke Dashboard
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="admin-users-page" className="mx-auto max-w-4xl px-5 py-8">
            <Link to="/admin" data-testid="back-to-admin-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft size={14} /> Kembali ke Dashboard
            </Link>
            <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-semibold tracking-tight">Kelola User</h1>
                    <p className="text-sm text-muted-foreground">Atur akun Super Admin dan Admin Produk.</p>
                </div>
                <button
                    data-testid="add-user-btn"
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-all"
                >
                    <Plus size={14} strokeWidth={1.75} /> Tambah User
                </button>
            </div>

            {error && <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

            <div className="mt-6 rounded-xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
                ) : (
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                            <tr className="border-b border-border bg-[hsl(var(--muted))] text-left text-xs uppercase tracking-[0.12em] text-muted-foreground">
                                <th className="px-4 py-3 font-medium">Nama / Email</th>
                                <th className="px-4 py-3 font-medium">Role</th>
                                <th className="px-4 py-3 font-medium text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const cfg = ROLE_LABEL[u.role] || ROLE_LABEL.product_admin;
                                const Icon = cfg.icon;
                                const isSelf = u.id === me?.id;
                                return (
                                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-b-0">
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{u.name || "—"}</div>
                                            <div className="text-xs text-muted-foreground">{u.email}{isSelf && " · (Anda)"}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cfg.tone}`}>
                                                <Icon size={11} strokeWidth={2} /> {cfg.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                data-testid={`user-edit-btn-${u.id}`}
                                                onClick={() => { setEditing(u); setShowForm(true); }}
                                                title="Edit"
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary transition-colors"
                                            >
                                                <Pencil size={16} strokeWidth={1.6} />
                                            </button>
                                            {!isSelf && (
                                                <button
                                                    data-testid={`user-delete-btn-${u.id}`}
                                                    onClick={() => onDelete(u)}
                                                    title="Hapus"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-destructive/10 text-destructive transition-colors"
                                                >
                                                    <Trash2 size={16} strokeWidth={1.6} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {showForm && (
                <UserFormDialog
                    user={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); load(); }}
                />
            )}
        </div>
    );
}

function UserFormDialog({ user, onClose, onSaved }) {
    const isEdit = !!user;
    const [email, setEmail] = useState(user?.email || "");
    const [name, setName] = useState(user?.name || "");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState(user?.role || "product_admin");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            if (isEdit) {
                const body = { name, role };
                if (password.trim()) body.password = password;
                await api.put(`/admin/users/${user.id}`, body);
            } else {
                await api.post("/admin/users", { email: email.trim(), name, password, role });
            }
            onSaved();
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="user-form-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <h3 className="font-heading text-lg font-semibold">{isEdit ? "Edit User" : "Tambah User"}</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="user-form-close"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="px-5 py-5 space-y-4">
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Email *</span>
                        <input
                            data-testid="user-form-email"
                            type="email"
                            required
                            disabled={isEdit}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary disabled:bg-secondary disabled:text-muted-foreground"
                        />
                        {isEdit && <p className="mt-1 text-xs text-muted-foreground">Email tidak dapat diubah.</p>}
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nama</span>
                        <input
                            data-testid="user-form-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">{isEdit ? "Password Baru (kosongkan jika tidak diubah)" : "Password * (min 6 karakter)"}</span>
                        <input
                            data-testid="user-form-password"
                            type="password"
                            minLength={isEdit ? 0 : 6}
                            required={!isEdit}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Role *</span>
                        <select
                            data-testid="user-form-role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="mt-2 w-full rounded-md border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary"
                        >
                            <option value="product_admin">Admin Produk (edit & upload gambar produk saja)</option>
                            <option value="super_admin">Super Admin (full access)</option>
                        </select>
                    </label>

                    {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary">Batal</button>
                        <button data-testid="user-form-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Simpan
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
